import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { CountrySelect } from '../../components/CountrySelect';
import { ErrorMsg, Modal } from '../../components/ui';
import { api, ApiError, errorText, newIdempotencyKey } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { ImportBatch, StudioRow } from '../../lib/types';

function lines(s: string) {
  return s
    .split(/[\n,]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function CheckList({ options, value, onChange, name }: { options: string[]; value: string[]; onChange: (v: string[]) => void; name: string }) {
  return (
    <div className="checklist" role="group" aria-label={name}>
      {options.map((o) => (
        <label key={o} className="check">
          <input type="checkbox" checked={value.includes(o)} onChange={(e) => onChange(e.target.checked ? [...value, o] : value.filter((x) => x !== o))} /> {o}
        </label>
      ))}
    </div>
  );
}

// --- Add studio ------------------------------------------------------------------------------

export function AddStudioDialog({ onClose }: { onClose: () => void }) {
  const { settings } = useSession();
  const navigate = useNavigate();
  const v = settings?.values;
  const [f, setF] = useState({ name: '', website: '', country: '', city: '', team_size: '', summary: '', socials: '', tags: '', note: '' });
  const [services, setServices] = useState<string[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [existing, setExisting] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setExisting(null);
    try {
      const row = await api<StudioRow>('/api/studios', {
        method: 'POST',
        body: {
          name: f.name,
          website: f.website || null,
          country: f.country || null,
          city: f.city || null,
          team_size: f.team_size || null,
          summary: f.summary || null,
          services,
          client_types: clients,
          socials: lines(f.socials),
          tags: lines(f.tags),
          note: f.note || null,
        },
      });
      navigate(`/competitors/${row.id}`);
    } catch (err) {
      setError(errorText(err));
      if (err instanceof ApiError && err.status === 409) {
        const m = /\(id ([0-9a-f-]{36})\)/.exec(err.detail);
        if (m) setExisting(m[1]);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Add a studio"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="add-studio" className="btn primary" disabled={busy || !f.name.trim()}>
            {busy ? 'Adding…' : 'Add studio'}
          </button>
        </>
      }
    >
      <form id="add-studio" onSubmit={submit} className="form-grid" aria-label="Add a studio">
        <label className="field">
          <span>Name *</span>
          <input required value={f.name} onChange={set('name')} autoFocus />
        </label>
        <label className="field">
          <span>Website</span>
          <input value={f.website} onChange={set('website')} placeholder="https://…" inputMode="url" />
        </label>
        <label className="field">
          <span>HQ country</span>
          <CountrySelect value={f.country} onChange={(country) => setF({ ...f, country })} />
        </label>
        <label className="field">
          <span>HQ city</span>
          <input value={f.city} onChange={set('city')} />
        </label>
        <label className="field">
          <span>Team size</span>
          <select value={f.team_size} onChange={set('team_size')}>
            <option value="">unknown</option>
            {(v?.team_size_bands ?? []).map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Tags (comma-separated)</span>
          <input value={f.tags} onChange={set('tags')} />
        </label>
        <fieldset className="field span2">
          <legend>Services</legend>
          <CheckList name="Services" options={v?.service_categories ?? []} value={services} onChange={setServices} />
        </fieldset>
        <fieldset className="field span2">
          <legend>Client types</legend>
          <CheckList name="Client types" options={v?.client_categories ?? []} value={clients} onChange={setClients} />
        </fieldset>
        <label className="field span2">
          <span>Summary</span>
          <textarea rows={2} value={f.summary} onChange={set('summary')} />
        </label>
        <label className="field span2">
          <span>Social profile URLs (one per line)</span>
          <textarea rows={2} value={f.socials} onChange={set('socials')} />
        </label>
        <label className="field span2">
          <span>Note</span>
          <textarea rows={2} value={f.note} onChange={set('note')} />
        </label>
      </form>
      <ErrorMsg>{error}</ErrorMsg>
      {existing && (
        <p className="small">
          <Link to={`/competitors/${existing}`}>Open the existing record</Link>
        </p>
      )}
      <p className="muted small">Fields you enter here are marked as entered by you; later website collection will only propose changes to them.</p>
    </Modal>
  );
}

// --- Import CSV ------------------------------------------------------------------------------

export function ImportDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [country, setCountry] = useState('');
  const [key, setKey] = useState(newIdempotencyKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ImportBatch | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      if (country.trim()) form.append('default_country', country.trim());
      const b = await api<ImportBatch>('/api/studios/import', { method: 'POST', form, idempotencyKey: key });
      setResult(b);
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const counts = result?.counts ?? {};
  const warnings = Array.isArray(counts.warnings) ? (counts.warnings as string[]) : [];

  return (
    <Modal
      title="Import studios from CSV"
      onClose={onClose}
      wide
      footer={
        result ? (
          <>
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setResult(null);
                setFile(null);
                setKey(newIdempotencyKey());
              }}
            >
              Import another file
            </button>
            <Link className="btn primary" to={`/competitors?batch_id=${result.id}&include_dismissed=1`} onClick={onClose}>
              Review this batch
            </Link>
          </>
        ) : (
          <>
            <button type="button" className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" form="import-form" className="btn primary" disabled={busy || !file}>
              {busy ? 'Importing…' : 'Import'}
            </button>
          </>
        )
      }
    >
      {!result && (
        <form id="import-form" onSubmit={submit} className="stack" aria-label="Import CSV">
          <p className="small">
            UTF-8 CSV, one studio per row, max 5 MB. <a href="/api/import/template.csv">Download the template</a> for the column names.
            Rows matching an existing studio (same website domain) are merged into it, not duplicated.
          </p>
          <label className="field">
            <span>CSV file</span>
            <input
              type="file"
              accept=".csv,text/csv"
              required
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setKey(newIdempotencyKey());
              }}
            />
          </label>
          <label className="field">
            <span>Default country for rows without one (optional)</span>
            <CountrySelect value={country} onChange={setCountry} emptyLabel="none" />
          </label>
        </form>
      )}
      <ErrorMsg>{error}</ErrorMsg>
      {result && (
        <div className="stack">
          {result.replayed && <p className="msg warn small">This file was already imported with the same request; showing the earlier result.</p>}
          <p>
            <strong>{result.label}</strong> · <span className="num">{String(counts.rows ?? 0)}</span> rows:{' '}
            <span className="num">{String(counts.created ?? 0)}</span> created, <span className="num">{String(counts.matched ?? 0)}</span> matched,{' '}
            <span className="num">{String(counts.suppressed ?? 0)}</span> suppressed, <span className="num">{String(counts.invalid ?? 0)}</span> invalid
          </p>
          {warnings.length > 0 && (
            <ul className="msg warn small">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
          <div className="table-wrap">
            <table className="table compact">
              <thead>
                <tr>
                  <th className="num">Line</th>
                  <th>Name</th>
                  <th>Outcome</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {(result.rows ?? []).map((r, i) => (
                  <tr key={i}>
                    <td className="num">{r.line ?? i + 1}</td>
                    <td>{r.studio_id ? <Link to={`/competitors/${r.studio_id}`}>{r.name ?? r.url ?? '—'}</Link> : r.name ?? r.url ?? '—'}</td>
                    <td>
                      <span className={`outcome o-${r.outcome}`}>{r.outcome}</span>
                    </td>
                    <td className="small">{r.message ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

// --- Discovery -------------------------------------------------------------------------------

export function DiscoveryDialog({ onClose }: { onClose: () => void }) {
  const { settings } = useSession();
  const v = settings?.values;
  const [country, setCountry] = useState('');
  const [cities, setCities] = useState('');
  const [services, setServices] = useState<string[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [maxCandidates, setMaxCandidates] = useState(Math.min(200, v?.collection.max_candidates ?? 30));
  const [pages, setPages] = useState(3);
  const [connector, setConnector] = useState<'manual_urls' | 'search'>('manual_urls');
  const [urls, setUrls] = useState('');
  const [label, setLabel] = useState('');
  const [key] = useState(newIdempotencyKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const urlList = urls
    .split(/\s+/)
    .map((u) => u.trim())
    .filter(Boolean);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api<{ job_id: string; created: boolean }>('/api/discovery-jobs', {
        method: 'POST',
        idempotencyKey: key,
        body: {
          connector,
          country: country.trim() || null,
          cities: lines(cities),
          services,
          team_sizes: teams,
          urls: urlList,
          max_candidates: maxCandidates,
          max_pages_per_candidate: pages,
          label: label.trim() || null,
        },
      });
      setJobId(r.job_id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New discovery"
      onClose={onClose}
      wide
      footer={
        jobId ? (
          <Link className="btn primary" to={`/jobs/${jobId}`} onClick={onClose}>
            Open the job
          </Link>
        ) : (
          <>
            <button type="button" className="btn ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" form="discovery-form" className="btn primary" disabled={busy || connector !== 'manual_urls' || urlList.length === 0}>
              {busy ? 'Starting…' : 'Start discovery'}
            </button>
          </>
        )
      }
    >
      {jobId ? (
        <p>
          Discovery job queued. The worker reads each website (up to {pages} pages per site), creates the studios and puts them in one batch for you to
          review. <Link to={`/jobs/${jobId}`} onClick={onClose}>Follow the job</Link>.
        </p>
      ) : (
        <form id="discovery-form" onSubmit={submit} className="form-grid" aria-label="New discovery">
          <fieldset className="field span2">
            <legend>Where do candidates come from?</legend>
            <label className="check block">
              <input type="radio" name="connector" checked={connector === 'manual_urls'} onChange={() => setConnector('manual_urls')} /> Paste website addresses
              <span className="muted small"> — the worker visits each one.</span>
            </label>
            <label className="check block disabled">
              <input type="radio" name="connector" disabled checked={connector === 'search'} onChange={() => setConnector('search')} /> Search provider
              <span className="muted small"> — not available: no search connector is configured. It needs an account and an API key with a search provider.</span>
            </label>
          </fieldset>
          <label className="field span2">
            <span>Website addresses (one per line) *</span>
            <textarea rows={5} value={urls} onChange={(e) => setUrls(e.target.value)} placeholder={'https://studio-one.bg\nhttps://studio-two.bg'} />
            <span className="muted small num">{urlList.length} address{urlList.length === 1 ? '' : 'es'}</span>
          </label>
          <label className="field">
            <span>Country</span>
            <CountrySelect value={country} onChange={setCountry} emptyLabel="choose a country" />
          </label>
          <label className="field">
            <span>Cities (optional, recorded only)</span>
            <input value={cities} onChange={(e) => setCities(e.target.value)} />
          </label>
          <label className="field">
            <span>Max candidates (1–200)</span>
            <input type="number" min={1} max={200} value={maxCandidates} onChange={(e) => setMaxCandidates(Number(e.target.value))} />
          </label>
          <label className="field">
            <span>Pages per candidate (1–10)</span>
            <input type="number" min={1} max={10} value={pages} onChange={(e) => setPages(Number(e.target.value))} />
          </label>
          <fieldset className="field span2">
            <legend>Services (optional; recorded with the job, pasted addresses are not filtered by it)</legend>
            <CheckList name="Services" options={v?.service_categories ?? []} value={services} onChange={setServices} />
          </fieldset>
          <fieldset className="field span2">
            <legend>Team sizes (optional; recorded with the job, not used as a filter)</legend>
            <CheckList name="Team sizes" options={v?.team_size_bands ?? []} value={teams} onChange={setTeams} />
          </fieldset>
          <label className="field span2">
            <span>Label (optional)</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} />
          </label>
        </form>
      )}
      <ErrorMsg>{error}</ErrorMsg>
    </Modal>
  );
}
